import { OutlookApiException } from 'common/exceptions/outlook/OutlookApiException';
import { OutlookWrongItemSelectedException } from 'common/exceptions/outlook/OutlookWrongItemSelectedException';
import { ClientListItem } from 'common/models/ClientListItem';
import { UserContextDTO } from 'common/models/UserContextDTO';
import { SupportedOfficeItem } from 'common/utils/SupportedOfficeItem';
import * as OutlookItemUtils from 'common/utils/outlookItemUtils';
import { sleep } from 'common/utils/sleep';
import CONFIG from 'config';
import $ from 'jquery';
import { DomainDetails } from '../models/DomainDetails';
import { GetDomainListResponse } from '../models/GetDomainListResponse';
import { ProjectInfo } from '../models/ProjectInfo';
import * as Api from '../utils/api';
import * as Auth from '../utils/authUtils';
import {
  getAdditionalStakeholders,
  getPrimaryStakeholders,
} from '../utils/contactUtils';
import * as OutlookAsyncUtils from '../utils/outlookAsyncUtils';
import * as Ui from '../utils/uiUtils';
import * as UserSettings from '../utils/user-settings';
import { getDiffInMinutes } from './../utils/dateUtils';
import * as AppAutosave from './autosave';
import { autosaveMigrationService } from './autosave-migration';
import { errorHandler } from './errorHandler';
import * as ItemProperties from './item-properties';
import * as AppSave from './save';
import * as AppNewVersionSstkAutosave from './sstk-autosave';

export let userContext: UserContextDTO = null;
export let clients: ClientListItem[] = [];
export let domains: DomainDetails[] = [];
export let projectGroupAvailable = false;
let savingMessage: boolean;
let autosavingMessages: boolean;
let migrationInProgress = false;
let migrationCompleted = false;
let lastAutosave: Date;
let autosaveTimerId: number;
let startingApp: boolean;
let currentItem: SupportedOfficeItem;
const TAG = 'APP1: ';

/**
 * Ensure migration is completed before proceeding with other operations
 */
export async function prepareAutosaveContactMigration(): Promise<void> {
  if (!CONFIG.isGraphApiEnabled) {
    console.log(TAG, 'Graph API is disabled. Migration will not be performed.');
    migrationCompleted = true;
    return;
  }

  if (migrationCompleted) {
    console.log(TAG, 'Migration already completed in this session');
    return;
  }

  if (migrationInProgress) {
    console.log(TAG, 'Migration already in progress, waiting...');
    while (migrationInProgress) {
      await sleep(100);
    }
    return;
  }

  // Check if user is authenticated before proceeding
  if (!Auth.isTokenValid()) {
    console.log(TAG, 'User not authenticated, skipping migration');
    migrationCompleted = true;
    return;
  }

  migrationInProgress = true;
  try {
    console.log(TAG, 'Starting autosave migration check');
    const result = await autosaveMigrationService.handleMigration(
      CONFIG.isNewSstkAutosaveContactEnabled
    );

    if (result.success) {
      console.log(TAG, 'Migration completed successfully:', result);
    } else {
      console.error(TAG, 'Migration failed:', result.error);
      // Don't throw error, allow app to continue but log the failure
    }

    migrationCompleted = true;
  } catch (error) {
    console.error(TAG, 'Migration failed with exception:', error);
    // Don't throw error, allow app to continue but mark as completed to avoid infinite retries
    migrationCompleted = true;
  } finally {
    migrationInProgress = false;
  }
}

export async function tryStart(item: SupportedOfficeItem) {
  currentItem = item;

  if (!currentItem) {
    return;
  }

  console.log(TAG, new Date(), 'Start tryStart');
  await waitForAllTasks();

  if (currentItem != item) {
    console.log(TAG, 'tryStart: cancelling because selected item has changed');
    return;
  }

  startingApp = true;
  try {
    console.log(TAG, new Date(), 'Starting app');
    await start(item);
  } catch (error) {
    // do not show this kind of errors because they occur often on the web
    if (error instanceof OutlookWrongItemSelectedException) {
      console.log(
        TAG,
        'OutlookWrongItemSelectedException has occurred when trying to start the application',
        error
      );
      Ui.showItemNotSupportedPage();
    } else if (currentItem != item) {
      console.log(
        TAG,
        'Failed to start the application for previous office item',
        error
      );
    } else {
      errorHandler(error, 'Failed to start the application');
    }
  }
  startingApp = false;
  console.log(TAG, new Date(), 'End tryStart');
}

export async function startAutosaveTimer(item: SupportedOfficeItem) {
  if (!CONFIG.isGraphApiEnabled) {
    console.log(
      TAG,
      'Graph API is disabled. Autosave timer will not be started.'
    );
    return;
  }

  if (autosaveTimerId) {
    console.log(TAG, 'Clearing existing autosave timer:', autosaveTimerId);
    clearInterval(autosaveTimerId);
    autosaveTimerId = undefined;
  }

  const sharedProperties = await OutlookAsyncUtils.getSharedProperties(item);

  if (sharedProperties) {
    console.log(TAG, 'Item is shared. Autosave timer will not be started.');
    return;
  }

  console.log(TAG, 'Invoking tryAutosaveIfDue...');
  await tryAutosaveIfDue();

  // tryAutosaveIfDue will be called frequently, but the autosave will still only happen every CONFIG.autosaveIntervalInMinutes minutes
  console.log(TAG, 'Setting interval for autosave.');
  autosaveTimerId = window.setInterval(() => {
    console.log(
      TAG,
      'Timer triggered (5 minute interval). Invoking tryAutosaveIfDue...'
    );
    tryAutosaveIfDue();
  }, 5 * 60 * 1000);

  console.log(TAG, 'Autosave timer started with ID:', autosaveTimerId);
}

export async function tryAutosaveIfDue() {
  if (autosavingMessages) {
    console.log(TAG, 'Already autosaving');
    return;
  }

  if (!isDueToAutosave()) {
    console.log(TAG, 'Not due to autosave. Last autosave:', lastAutosave);
    return;
  }

  console.log(TAG, new Date(), 'Start tryAutosaveIfDue:');

  await waitForAllTasks();

  // Check again after waiting for pending tasks as another autosave may have been started in the interim
  if (autosavingMessages) {
    console.log(TAG, 'Already autosaving');
    return;
  }

  autosavingMessages = true;

  try {
    console.log(TAG, new Date(), 'Autosave new messages:');

    if (CONFIG.isNewSstkAutosaveContactEnabled) {
      await AppNewVersionSstkAutosave.autosaveNewMessages();
    } else {
      await AppAutosave.autosaveNewMessages();
    }
  } catch (error) {
    errorHandler(error, 'Failed to autosave new messages');
  }

  autosavingMessages = false;

  console.log(TAG, new Date(), 'End tryAutosaveIfDue:');
}

export async function trySaveMessage(item: SupportedOfficeItem) {
  console.log(TAG, new Date(), 'Start trySaveMessage');

  await waitForAllTasks();

  savingMessage = true;
  try {
    console.log(TAG, new Date(), 'Saving message');
    await AppSave.saveMessage(item);
  } catch (error) {
    const message = 'Failed to save the document';
    if (error instanceof OutlookApiException) {
      errorHandler(error, undefined);
    } else {
      errorHandler(error, message);
    }
  }

  if (UserSettings.getAutosave()) {
    resetAutosavePeriod();
  }

  savingMessage = false;

  console.log(TAG, new Date(), 'End trySaveMessage');
}

export async function start(item: SupportedOfficeItem) {
  console.log(TAG, `Starting processing for item: ${item.subject}`);

  const sharedProperties = await OutlookAsyncUtils.getSharedProperties(item);
  console.log(
    TAG,
    `Retrieved shared properties for item: ${item.subject}`,
    sharedProperties
  );

  if (await OutlookItemUtils.isReadOnlySharedItem(item, sharedProperties)) {
    console.log(
      TAG,
      `Item is a read-only shared item. Showing not supported message for item: ${item.subject}`
    );
    Ui.showReadOnlySharedItemNotSupported();
  } else if (await ItemProperties.isItemSavedThroughApi(item)) {
    console.log(
      TAG,
      `Item is already saved through API. Showing saved message for item: ${item.subject}`
    );
    Ui.showMessageSaved();
  } else if (
    !CONFIG.isNewSstkAutosaveContactEnabled &&
    (await AppAutosave.isAutosaved(item))
  ) {
    console.log(
      TAG,
      `Item is already autosaved. Showing autosaved message for item: ${item.subject}`
    );
    Ui.showMessageAutosaved();
  } else if (
    CONFIG.isNewSstkAutosaveContactEnabled &&
    (await AppNewVersionSstkAutosave.isAutosaved(item))
  ) {
    console.log(
      TAG,
      `Item is already autosaved. Showing autosaved message for item: ${item.subject}`
    );
    Ui.showMessageAutosaved();
  } else if (await OutlookAsyncUtils.isRecurringMaster(item)) {
    console.log(
      TAG,
      `Item is a recurring master. Showing series event not supported message for item: ${item.subject}`
    );
    Ui.showSeriesEventNotSupported();
  } else if (await OutlookItemUtils.isSavedMeeting(item, sharedProperties)) {
    console.log(
      TAG,
      `Item is a saved meeting. Showing saved message for item: ${item.subject}`
    );
    Ui.showMessageSaved();
  } else {
    console.log(
      TAG,
      `None of the conditions met. Showing primary stakeholders info for item: ${item.subject}`
    );
    await Ui.showPrimaryStakeholdersInfo(item);
  }

  console.log(TAG, `Finished processing for item: ${item.subject}`);
}

export async function showStakeholdersForm(item: SupportedOfficeItem) {
  const allRadio = $('input[name="save-all-future-email"]');

  allRadio.each(function () {
    if ($(this).val() == 'one') {
      $(this).prop('checked', true);
    }
    $(this).prop('disabled', UserSettings.getAutosave());
  });

  const sharedProperties = await OutlookAsyncUtils.getSharedProperties(item);

  const [primaryStakeholders, additionalStakeholders] = await Promise.all([
    getPrimaryStakeholders(item, sharedProperties),
    getAdditionalStakeholders(item, sharedProperties),
  ]);

  Ui.showStakeholdersForm(
    primaryStakeholders,
    additionalStakeholders,
    item.itemType,
    item.attachments
  );
}

export async function getClientList() {
  clients = (await Api.getClientList()).Clients;
  if (clients.length > 1) {
    console.log(TAG, 'GET Client List -> Form Client Show');
    $('#form-client').show();
  } else {
    console.log(TAG, 'GET Client List -> Form Client Hide');
    $('#form-client').hide();
  }
}

export async function refreshClients() {
  Ui.refreshClients(clients);
}

export async function refreshDomains(clientID: number) {
  const domainsInfo = await Api.getDomainList(clientID);
  domains = domainsInfo.DomainDetails;
  projectGroupAvailable = domainsInfo.ProjectGroupAvailable;
  const favouriteDomainAndGroup = getFavDomainAndGroup(domainsInfo);
  Ui.refreshDomains(
    domainsInfo.DomainDetails,
    favouriteDomainAndGroup,
    domainsInfo.ProjectGroupAvailable
  );
}

/** Returns ID of first available project */
export async function selectClientAnyProjectId(
  clientID: number
): Promise<string> {
  const domainsInfo = await Api.getDomainList(clientID);
  domains = domainsInfo.DomainDetails;
  projectGroupAvailable = domainsInfo.ProjectGroupAvailable;

  for (const domain of domains) {
    for (const project of domain.Projects) {
      return project.ID;
    }
    for (const group of domain.ProjectGroups) {
      for (const project of group.Projects) {
        return project.ID;
      }
    }

    for (const project of domain.Projects) {
      return project.ID;
    }
  }
}

export async function switchClient(client: ClientListItem): Promise<string> {
  UserSettings.setClientCode(client.Name);

  // because token can't be refreshed if project isn't selected,
  // we'll select first available project and refresh token for it
  const projectId = await selectClientAnyProjectId(client.ClientID);
  if (projectId) {
    UserSettings.setProject(projectId);
    await Auth.refreshToken();
  }
  return projectId;
}

/** Returns ID of first available project */
export async function selectAnyProjectId(): Promise<string> {
  const pID = await selectClientAnyProjectId(0);
  return pID;
}

export function logout() {
  localStorage.clear();
  Ui.showLogoutSuccessDialog();
}

function getFavDomainAndGroup(domainList: GetDomainListResponse) {
  if (!domainList.FavouriteProjectDetails) return null;

  const favProjectName = domainList.FavouriteProjectDetails.ProjectName;
  const projectGroupAvailable = domainList.ProjectGroupAvailable;
  const domains = domainList.DomainDetails;

  let favouriteProject: ProjectInfo = null;

  const projectInfos: ProjectInfo[] = [];

  domains.forEach(function (domain) {
    if (projectGroupAvailable) {
      domain.ProjectGroups.forEach(function (projectGroup) {
        projectGroup.Projects.forEach(function (project) {
          projectInfos.push({
            domainId: domain.ID,
            projectGroupId: projectGroup.ID,
            projectId: project.ID,
            project: project,
          });
        });
      });
    } else {
      domain.Projects.forEach(function (project) {
        projectInfos.push({
          domainId: domain.ID,
          projectGroupId: '-1',
          projectId: project.ID,
          project: project,
        });
      });
    }
  });

  projectInfos.find(function (projectInfo) {
    if (projectInfo.project.ProjectName == favProjectName) {
      favouriteProject = {
        domainId: projectInfo.domainId,
        projectGroupId: projectInfo.projectGroupId,
        projectId: projectInfo.project.ID,
        project: projectInfo.project,
      };
    }
  });
  return favouriteProject;
}

function isDueToAutosave() {
  const diffMinutes = getDiffInMinutes(new Date(), lastAutosave || new Date(0));

  const isDue = diffMinutes > CONFIG.autosaveIntervalInMinutes;
  return isDue;
}

/** Resets the time of last autosave to trigger it on the next app start */
function resetAutosavePeriod() {
  lastAutosave = undefined;
}

async function waitForAllTasks() {
  while (
    savingMessage ||
    autosavingMessages ||
    startingApp ||
    migrationInProgress
  ) {
    console.log(
      TAG,
      new Date(),
      'Waiting for:',
      'savingMessage:',
      savingMessage,
      'autosavingMessages:',
      autosavingMessages,
      'startingApp:',
      startingApp,
      'migrationInProgress:',
      migrationInProgress
    );
    await sleep(50);
  }
}

export async function getUserContext(): Promise<UserContextDTO> {
  const context = await Api.getUserContext();
  userContext = context;
  return context;
}
